import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["formattingIsPresent"];
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import { injectIntl } from 'react-intl-next';
import { useSharedPluginState, sharedPluginStateHookMigratorFactory } from '@atlaskit/editor-common/hooks';
import { ToolbarSize } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { DefaultFloatingToolbarButtonsNext } from './Toolbar/constants';
import { FormattingTextDropdownMenu } from './Toolbar/dropdown-menu';
import { hasMultiplePartsWithFormattingInSelection } from './Toolbar/formatting-in-selection-utils';
import { useClearIcon } from './Toolbar/hooks/clear-formatting-icon';
import { useFormattingIcons } from './Toolbar/hooks/formatting-icons';
import { useIconList } from './Toolbar/hooks/use-icon-list';
import { SingleToolbarButtons } from './Toolbar/single-toolbar-buttons';
import { ToolbarType } from './Toolbar/types';
var FloatingToolbarSettings = {
  disabled: false,
  isReducedSpacing: true,
  shouldUseResponsiveToolbar: false,
  toolbarSize: ToolbarSize.S,
  hasMoreButton: false,
  moreButtonLabel: '',
  toolbarType: ToolbarType.FLOATING
};
var useSharedState = sharedPluginStateHookMigratorFactory(function (api) {
  var isInitialised = useSharedPluginStateSelector(api, 'textFormatting.isInitialised');
  var formattingIsPresent = useSharedPluginStateSelector(api, 'textFormatting.formattingIsPresent');
  var emActive = useSharedPluginStateSelector(api, 'textFormatting.emActive');
  var emDisabled = useSharedPluginStateSelector(api, 'textFormatting.emDisabled');
  var emHidden = useSharedPluginStateSelector(api, 'textFormatting.emHidden');
  var codeActive = useSharedPluginStateSelector(api, 'textFormatting.codeActive');
  var codeDisabled = useSharedPluginStateSelector(api, 'textFormatting.codeDisabled');
  var codeHidden = useSharedPluginStateSelector(api, 'textFormatting.codeHidden');
  var underlineActive = useSharedPluginStateSelector(api, 'textFormatting.underlineActive');
  var underlineDisabled = useSharedPluginStateSelector(api, 'textFormatting.underlineDisabled');
  var underlineHidden = useSharedPluginStateSelector(api, 'textFormatting.underlineHidden');
  var strikeActive = useSharedPluginStateSelector(api, 'textFormatting.strikeActive');
  var strikeDisabled = useSharedPluginStateSelector(api, 'textFormatting.strikeDisabled');
  var strikeHidden = useSharedPluginStateSelector(api, 'textFormatting.strikeHidden');
  var strongActive = useSharedPluginStateSelector(api, 'textFormatting.strongActive');
  var strongDisabled = useSharedPluginStateSelector(api, 'textFormatting.strongDisabled');
  var strongHidden = useSharedPluginStateSelector(api, 'textFormatting.strongHidden');
  var superscriptActive = useSharedPluginStateSelector(api, 'textFormatting.superscriptActive');
  var superscriptDisabled = useSharedPluginStateSelector(api, 'textFormatting.superscriptDisabled');
  var superscriptHidden = useSharedPluginStateSelector(api, 'textFormatting.superscriptHidden');
  var subscriptActive = useSharedPluginStateSelector(api, 'textFormatting.subscriptActive');
  var subscriptDisabled = useSharedPluginStateSelector(api, 'textFormatting.subscriptDisabled');
  var subscriptHidden = useSharedPluginStateSelector(api, 'textFormatting.subscriptHidden');
  return {
    isInitialised: Boolean(isInitialised),
    formattingIsPresent: formattingIsPresent,
    emActive: emActive,
    emDisabled: emDisabled,
    emHidden: emHidden,
    codeActive: codeActive,
    codeDisabled: codeDisabled,
    codeHidden: codeHidden,
    underlineActive: underlineActive,
    underlineDisabled: underlineDisabled,
    underlineHidden: underlineHidden,
    strikeActive: strikeActive,
    strikeDisabled: strikeDisabled,
    strikeHidden: strikeHidden,
    strongActive: strongActive,
    strongDisabled: strongDisabled,
    strongHidden: strongHidden,
    superscriptActive: superscriptActive,
    superscriptDisabled: superscriptDisabled,
    superscriptHidden: superscriptHidden,
    subscriptActive: subscriptActive,
    subscriptDisabled: subscriptDisabled,
    subscriptHidden: subscriptHidden
  };
}, function (api) {
  var _useSharedPluginState = useSharedPluginState(api, ['textFormatting']),
    textFormattingState = _useSharedPluginState.textFormattingState;
  return {
    isInitialised: Boolean(textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.isInitialised),
    formattingIsPresent: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.formattingIsPresent,
    emActive: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.emActive,
    emDisabled: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.emDisabled,
    emHidden: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.emHidden,
    codeActive: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.codeActive,
    codeDisabled: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.codeDisabled,
    codeHidden: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.codeHidden,
    underlineActive: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.underlineActive,
    underlineDisabled: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.underlineDisabled,
    underlineHidden: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.underlineHidden,
    strikeActive: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.strikeActive,
    strikeDisabled: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.strikeDisabled,
    strikeHidden: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.strikeHidden,
    strongActive: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.strongActive,
    strongDisabled: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.strongDisabled,
    strongHidden: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.strongHidden,
    superscriptActive: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.superscriptActive,
    superscriptDisabled: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.superscriptDisabled,
    superscriptHidden: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.superscriptHidden,
    subscriptActive: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.subscriptActive,
    subscriptDisabled: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.subscriptDisabled,
    subscriptHidden: textFormattingState === null || textFormattingState === void 0 ? void 0 : textFormattingState.subscriptHidden
  };
});
var FloatingToolbarTextFormat = function FloatingToolbarTextFormat(_ref) {
  var api = _ref.api,
    editorAnalyticsAPI = _ref.editorAnalyticsAPI,
    editorView = _ref.editorView,
    intl = _ref.intl;
  var textFormattingState = useSharedState(api);
  var formattingIsPresent = textFormattingState.formattingIsPresent,
    formattingIconState = _objectWithoutProperties(textFormattingState, _excluded);
  var defaultIcons = useFormattingIcons({
    schema: editorView.state.schema,
    intl: intl,
    isToolbarDisabled: FloatingToolbarSettings.disabled,
    editorAnalyticsAPI: editorAnalyticsAPI,
    textFormattingState: formattingIconState,
    toolbarType: FloatingToolbarSettings.toolbarType
  });
  var hasMultiplePartsWithFormattingSelected;
  if (editorExperiment('platform_editor_controls', 'variant1') && fg('platform_editor_controls_patch_7')) {
    var selection = editorView.state.selection;
    var from = selection.from,
      to = selection.to;
    var selectedContent = selection instanceof TextSelection ? editorView.state.doc.slice(from, to).content.content.slice() : undefined;
    hasMultiplePartsWithFormattingSelected = hasMultiplePartsWithFormattingInSelection({
      selectedContent: selectedContent
    });
  }
  var _useIconList = useIconList({
      icons: defaultIcons,
      iconTypeList: DefaultFloatingToolbarButtonsNext,
      shouldUnselect: hasMultiplePartsWithFormattingSelected
    }),
    dropdownItems = _useIconList.dropdownItems,
    singleItems = _useIconList.singleItems;
  var clearIcon = useClearIcon({
    formattingPluginInitialised: textFormattingState.isInitialised,
    formattingIsPresent: formattingIsPresent,
    intl: intl,
    editorAnalyticsAPI: editorAnalyticsAPI,
    toolbarType: FloatingToolbarSettings.toolbarType
  });
  var items = useMemo(function () {
    if (!clearIcon) {
      return [{
        items: dropdownItems
      }];
    }
    return [{
      items: dropdownItems
    }, {
      items: [clearIcon]
    }];
  }, [clearIcon, dropdownItems]);
  return jsx(React.Fragment, null, jsx(SingleToolbarButtons, {
    items: singleItems,
    editorView: editorView,
    isReducedSpacing: false,
    shouldUnselect: hasMultiplePartsWithFormattingSelected
  }), jsx(FormattingTextDropdownMenu, {
    editorView: editorView,
    items: items,
    isReducedSpacing: editorExperiment('platform_editor_controls', 'variant1') ? false : FloatingToolbarSettings.isReducedSpacing,
    moreButtonLabel: FloatingToolbarSettings.moreButtonLabel,
    hasFormattingActive: FloatingToolbarSettings.hasMoreButton,
    hasMoreButton: FloatingToolbarSettings.hasMoreButton,
    intl: intl,
    toolbarType: FloatingToolbarSettings.toolbarType,
    shouldUnselect: hasMultiplePartsWithFormattingSelected
  }));
};
export var FloatingToolbarTextFormalWithIntl = injectIntl(FloatingToolbarTextFormat);